import { PromoToolSlot } from '@/types/amplience';
import { getDomain } from './domain';
import { isScheduledItemExpired } from './isScheduledItemExpired';

const ACTIVE_PROMO_CODE_KEY = 'activatedVoucher';
const ACTIVE_PROMO_NAME_KEY = 'activatedPromo';
const PROMO_POPUP_CLOSED_KEY = 'promoPopupClosed';
// Lists the active vouchers that were activated via a `?couponCode=` URL param.
// Set as a session cookie so the "duration of the session" scope falls out for free.
const URL_COUPON_MARKER_KEY = 'activatedVoucherFromUrl';
const MAX_CLOSED_PROMOS = 5;

const getCookieKeyWithEnv = (key: string) => {
  const hostname = window.location.hostname;

  if (hostname.startsWith('www.spreadshirt')) return key;
  if (hostname.startsWith('next.spreadshirt')) return `${key}-staging`;
  return `${key}-dev`;
};

const getCookieValue = (key: string): string | null | undefined => {
  if (typeof window === 'undefined') return null;
  try {
    const cookie = document.cookie.split('; ').find((row) => row.startsWith(`${key}=`));
    return cookie ? cookie.split('=')[1] : null;
  } catch {
    return null;
  }
};

const setCookie = (key: string, value: string | null, expires: Date | null) => {
  if (typeof window === 'undefined') return;
  try {
    const domain = getDomain();
    if (value) {
      // Omit `expires` entirely when none is given → a proper browser session cookie.
      const expiresPart = expires ? `expires=${expires.toUTCString()}; ` : '';
      document.cookie = `${key}=${value}; path=/; ${expiresPart}Domain=${domain}`;
    } else {
      document.cookie = `${key}=; path=/; max-age=0; Domain=${domain}`;
    }
  } catch {
    // do nothing
  }
};

export const isPromoExired = (promoSlot: PromoToolSlot | null): boolean => {
  return promoSlot && (promoSlot.content._meta.lifecycle || promoSlot.content._meta.edition)
    ? isScheduledItemExpired(promoSlot.content._meta)
    : true;
};

export const MAX_ACTIVE_COUPONS = 5;
// Codes are stored comma-separated in a cookie, so commas, `;` and whitespace must never get in.
const COUPON_CODE_PATTERN = /^[A-Za-z0-9_-]{1,50}$/;
// Pre-multi-coupon value of the URL marker: the single code in `activatedVoucher` came from a URL.
const LEGACY_URL_MARKER = '1';
const COUPON_SEPARATOR = ',';

export type CouponSource = 'promo' | 'url';
export type ActiveCoupon = { code: string; source: CouponSource };

export const isValidCouponCode = (code: string): boolean => COUPON_CODE_PATTERN.test(code);

const parseCodeList = (value: string | null | undefined): string[] =>
  value?.split(COUPON_SEPARATOR).filter(isValidCouponCode) ?? [];

// `activatedVoucher` holds every active code; `activatedVoucherFromUrl` lists the URL-sourced ones.
export const getActiveCoupons = (): ActiveCoupon[] => {
  const codes = parseCodeList(getCookieValue(ACTIVE_PROMO_CODE_KEY));
  const marker = getCookieValue(URL_COUPON_MARKER_KEY);
  const urlCodes = marker === LEGACY_URL_MARKER ? codes : parseCodeList(marker);

  return codes.map((code) => ({ code, source: urlCodes.includes(code) ? 'url' : 'promo' }));
};

const mergeCoupon = (coupons: ActiveCoupon[], added: ActiveCoupon): ActiveCoupon[] => {
  const existing = coupons.find((c) => c.code === added.code);
  if (!existing) return [...coupons, added].slice(-MAX_ACTIVE_COUPONS);
  // URL-sourced wins: such a code is always sent to the backend, never downgraded to promo.
  const source: CouponSource = existing.source === 'url' ? 'url' : added.source;
  return coupons.map((c) => (c.code === added.code ? { code: c.code, source } : c));
};

const writeActiveCoupons = (coupons: ActiveCoupon[], expires: Date | null) => {
  const urlCodes = coupons.filter((c) => c.source === 'url').map((c) => c.code);
  const cookieExpires = urlCodes.length ? null : expires;

  setCookie(
    ACTIVE_PROMO_CODE_KEY,
    coupons.map((c) => c.code).join(COUPON_SEPARATOR),
    cookieExpires
  );
  setCookie(URL_COUPON_MARKER_KEY, urlCodes.join(COUPON_SEPARATOR) || null, null);
};

// Adds a coupon to the active list. URL coupons have no known expiry, so as soon as one is in the
// list the cookie becomes a session cookie; otherwise it expires with the latest activated promo.
// `replace` (single-coupon mode of the multi-coupon A/B test) keeps only the new coupon.
// Returns the resulting list of active coupons.
export const addActiveCoupon = (
  code: string,
  source: CouponSource,
  expires: Date | null,
  { replace = false }: { replace?: boolean } = {}
): ActiveCoupon[] => {
  const current = getActiveCoupons();
  if (!isValidCouponCode(code)) return current;

  const coupons = replace ? [{ code, source }] : mergeCoupon(current, { code, source });
  writeActiveCoupons(coupons, expires);
  return coupons;
};

export const clearActiveCoupons = () => {
  setCookie(ACTIVE_PROMO_CODE_KEY, null, null);
  setCookie(URL_COUPON_MARKER_KEY, null, null);
};

// Single-coupon mode: several coupons can only be left over from the multi-coupon variant (e.g.
// after the A/B test stopped). Start over without them rather than guess which one to keep.
export const clearLeftoverActiveCoupons = (): ActiveCoupon[] => {
  const coupons = getActiveCoupons();
  if (coupons.length <= 1) return coupons;

  clearActiveCoupons();
  return [];
};

export const getActivePromoName = () => {
  return getCookieValue(ACTIVE_PROMO_NAME_KEY);
};

export const setActivePromoName = (name: string | null, expires: Date | null) => {
  setCookie(ACTIVE_PROMO_NAME_KEY, name, expires);
};

const getClosedPromoCodes = (): string[] => {
  const keyWithEnv = getCookieKeyWithEnv(PROMO_POPUP_CLOSED_KEY);
  const value = getCookieValue(keyWithEnv);
  if (!value) return [];
  try {
    return value.split(',').filter(Boolean);
  } catch {
    return [];
  }
};

export const isPromoPopupClosed = (promoCode: string): boolean => {
  const closedCodes = getClosedPromoCodes();
  return closedCodes.includes(promoCode);
};

export const setPromoPopupClosed = (promoCode: string, expires: Date | null) => {
  const closedCodes = getClosedPromoCodes();

  if (closedCodes.includes(promoCode)) return;
  closedCodes.push(promoCode);
  if (closedCodes.length > MAX_CLOSED_PROMOS) {
    closedCodes.shift();
  }
  const keyWithEnv = getCookieKeyWithEnv(PROMO_POPUP_CLOSED_KEY);
  setCookie(keyWithEnv, closedCodes.join(','), expires);
};

/**
 * Returns a 6 month expiry date for the promo popup cookie.
 * Note: Safari ITP may set cookies to 7 days for inactive users,
 * so some Safari users might see the popup again after 7 days of inactivity.
 */
export const getPromoExpiryDate = (): Date => {
  const expiryDate = new Date();
  expiryDate.setMonth(expiryDate.getMonth() + 6);
  return expiryDate;
};
